import { AitRowGroupData } from "./AitRowGroupData";
import {
  AitHeaderGroupData,
  AitLocationBodyRow,
  AitLocationHeaderRow,
  AitTablePosition,
} from "./interface";
import { trDuplicate } from "./trDuplicate";

/**
 * Duplicate a row in either the header or a body row group.
 *
 * When duplication is allowed (the target group's length is less than
 * `newLength`), the specified row is deep-cloned and inserted immediately
 * after the original. RowSpan/colSpan adjustments are applied so the table
 * structure remains consistent.
 *
 * @template T - The row/cell payload type
 * @param headerData - Header group data or `false` when not present
 * @param bodyData - Array of body row groups
 * @param location - Location describing the row to duplicate (headerRow or bodyRow)
 * @param newLength - Minimum required length after duplication; if current length >= newLength no change is made
 * @returns Object with updated `headerData` and `bodyData`
 */
export const trDuplicateRow = <T>(
  headerData: AitHeaderGroupData<T> | false,
  bodyData: AitRowGroupData<T>[],
  location: AitLocationHeaderRow | AitLocationBodyRow,
  newLength: number,
): { headerData: AitHeaderGroupData<T> | false; bodyData: AitRowGroupData<T>[] } => {
  /* istanbul ignore else */
  if (
    location.position === AitTablePosition.headerRow &&
    headerData !== false &&
    headerData.rows.length < newLength &&
    headerData.rows.find((row) => row.aitid === location.rowId)
  ) {
    const rowIndex = headerData.rows.findIndex((row) => row.aitid === location.rowId);
    const newRows = [...headerData.rows];
    const duplicateRow = trDuplicate(headerData.rows[rowIndex]);
    /* istanbul ignore else */
    if (duplicateRow) {
      // Check rowSpans
      const cols = duplicateRow.cells.length;
      for (let ci = 0; ci < cols; ci++) {
        // Change any duplicate row rowSpans to zero if they are not 1
        if (duplicateRow.cells[ci].rowSpan !== 1) {
          duplicateRow.cells[ci].rowSpan = 0;
          // Look for the previous cell to expand
          let riUp = 0;
          while (riUp <= rowIndex && newRows[rowIndex - riUp].cells[ci].rowSpan === 0) riUp++;
          // Get new length based on number of cells below with zero rowSpan
          let zeroCells = 1;
          while (
            newRows[rowIndex - riUp + zeroCells] &&
            newRows[rowIndex - riUp + zeroCells].cells[ci].rowSpan === 0
          ) {
            zeroCells++;
          }
          newRows[rowIndex - riUp].cells[ci] = {
            ...newRows[rowIndex - riUp].cells[ci],
            rowSpan: zeroCells + 1,
          };
          newRows[rowIndex - riUp] = {
            ...newRows[rowIndex - riUp],
            cells: [...newRows[rowIndex - riUp].cells],
          };
        }
      }
      newRows.splice(rowIndex + 1, 0, duplicateRow);
      headerData = { ...headerData, rows: newRows };
    }
  } else if (
    location.position === AitTablePosition.bodyRow &&
    bodyData[location.rowGroup].rows.length < newLength &&
    bodyData[location.rowGroup].rows.find((row) => row.aitid === location.rowId)
  ) {
    const rowGroupIndex = location.rowGroup;
    const rowIndex = bodyData[rowGroupIndex].rows.findIndex((row) => row.aitid === location.rowId);
    const duplicateRow = trDuplicate(bodyData[rowGroupIndex].rows[rowIndex]);
    /* istanbul ignore else */
    if (duplicateRow) {
      const newRows = [
        ...bodyData[rowGroupIndex].rows.slice(0, rowIndex + 1),
        duplicateRow,
        ...bodyData[rowGroupIndex].rows.slice(rowIndex + 1),
      ];
      bodyData = [
        ...bodyData.slice(0, rowGroupIndex),
        { ...bodyData[rowGroupIndex], rows: newRows },
        ...bodyData.slice(rowGroupIndex + 1),
      ];
    }
  }
  return { headerData, bodyData };
};
